// App.tsx
import React, { createContext, useContext, useState } from "react";
import { Toaster } from "react-hot-toast";
import type { IconBaseProps } from "react-icons";
import { FaCog } from "react-icons/fa";
import {
  Navigate,
  NavLink,
  Route,
  BrowserRouter as Router,
  Routes,
} from "react-router-dom";
import DashboardPage from "./dashboard/DashboardPage";
import "./index.css";
import LoginPage from "./login/LoginPage";
import MapPage from "./map/MapPage";
import { DetailsFilterProvider } from "./RouteDetails/DetailsFilterContext";
import RouteDetailsPage from "./RouteDetails/RouteDetailsPage";
import SettingsPage from "./settings/SettingsPage";

const CogIcon = FaCog as unknown as React.FC<IconBaseProps>;

// Create Auth Context
export const AuthContext = createContext<{
  isLoggedIn: boolean;
  login: () => void;
  logout: () => void;
}>({
  isLoggedIn: false,
  login: () => { },
  logout: () => { },
});

// Auth Provider Component
const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isLoggedIn, setIsLoggedIn] = useState(() =>
    localStorage.getItem("accessToken") ? true : false
  );

  const login = () => setIsLoggedIn(true);
  const logout = () => {
    localStorage.removeItem("accessToken");
    setIsLoggedIn(false);
  };

  return (
    <AuthContext.Provider value={{ isLoggedIn, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Protected Route Component
const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { isLoggedIn } = useContext(AuthContext);
  return isLoggedIn ? <>{children}</> : <Navigate to="/login" replace />;
};

const App: React.FC = () => {
  return (
    <AuthProvider>
      <Router>
        <div className="min-h-screen bg-gray-100 flex flex-col">
          {/* Updated Header */}
          <header className="bg-white shadow-md sticky top-0 z-50">
            <div className="container mx-auto px-6 py-6 flex items-center justify-between">
              {/* Left Column: empty */}
              <div className="w-1/3"></div>

              {/* Center Column: Navigation */}
              <div className="w-1/3 flex justify-center ml-2">
                <nav className="flex items-center space-x-2">
                  <NavLink
                    to="/"
                    end
                    className={({ isActive }) =>
                      isActive
                        ? "text-[#2563eb] font-bold text-lg"
                        : "text-gray-800 font-bold text-lg"
                    }
                  >
                    Dashboard
                  </NavLink>
                  <span className="text-gray-500 font-bold text-2xl">|</span>
                  <NavLink
                    to="/map"
                    className={({ isActive }) =>
                      isActive
                        ? "text-[#2563eb] font-bold text-lg"
                        : "text-gray-800 font-bold text-lg"
                    }
                  >
                    Map
                  </NavLink>
                </nav>
              </div>

              {/* Right Column: Settings Gear */}
              <div className="w-1/3 flex justify-end">
                <NavLink
                  to="/settings"
                  className={({ isActive }) =>
                    isActive
                      ? "text-[#2563eb] text-2xl"
                      : "text-gray-500 text-2xl"
                  }
                >
                  <FaCog />
                </NavLink>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-grow">
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route
                path="/"
                element={
                  <ProtectedRoute>
                    <DashboardPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/map"
                element={
                  <ProtectedRoute>
                    <MapPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/route/details/:id"
                element={
                  <ProtectedRoute>
                    <DetailsFilterProvider>
                      <RouteDetailsPage />
                    </DetailsFilterProvider>
                  </ProtectedRoute>
                }
              />
              <Route
                path="/settings"
                element={
                  <ProtectedRoute>
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
            </Routes>
          </main>
        </div>
        <Toaster position="bottom-center" />
      </Router>
    </AuthProvider>
  );
};

export default App;
